// Maps each search index's hit to the one row shape the condensed layout's wide results table renders.
import { policyTypeNames } from '@ourbranch/lookups';

import { awsDateToDateFormatter, dateTimeFormatterInUserTimezone } from 'core/helpers/formatters';
import { staffOfferLink } from 'core/helpers/bridge-link-opened';
import { getPolicyStatus, getPolicyStatusLabel, PolicyStatus } from 'core/helpers/policy-status';
import { customerMatchedOn, offerMatchedOn, policyMatchedOn } from '../matched-on';
import { formatEffectiveDateRange } from '../policies-results/policies-results.helpers';
import { deriveEligibleOffers } from '../offers-results/offer-results.helpers';

const joinPresent = (parts, separator) => parts.filter(Boolean).join(separator);

// Same colors as the classic policy status circles.
const POLICY_STATUS_TONE = {
  [PolicyStatus.Active]: 'positive',
  [PolicyStatus.Future]: 'pending',
  [PolicyStatus.InCancellation]: 'negative',
  [PolicyStatus.Cancelled]: 'negative',
  [PolicyStatus.Inactive]: 'neutral'
};

export const customerRow = (hit) => {
  const attributes = hit?.attributes ?? {};
  return {
    key: `customer-${hit.Username}`,
    type: 'Customer',
    name: joinPresent([attributes.name, attributes.family_name], ' '),
    location: joinPresent([attributes.custom_city, attributes.custom_state], ', '),
    id: hit.Username,
    to: `/customer/${hit.Username}`,
    status: null,
    matchedOn: customerMatchedOn(hit),
    // format() throws on an invalid date, and policy-sourced customers can lack one.
    date: hit.UserCreateDate ? awsDateToDateFormatter(new Date(hit.UserCreateDate)) : ''
  };
};

export const policyRow = (hit) => {
  const details = hit.policyDetails ?? {};
  const people = details.drivers ?? details.people;
  const person = people?.find((candidate) => candidate?.isPrimary) ?? {};
  const location = details.location ?? details.cars?.[0]?.garageLocation ?? {};
  const status = getPolicyStatus(hit);
  return {
    key: `policy-${hit.id}`,
    type: 'Policy',
    name: joinPresent([joinPresent([person.firstName, person.lastName], ' '), policyTypeNames[hit.policyType]], ' · '),
    location: joinPresent([location.city, location.state || hit.state], ', '),
    id: hit.id,
    to: `/customer/${hit.accountId}/policy/${hit.id}`,
    status: { label: getPolicyStatusLabel(hit, status), tone: POLICY_STATUS_TONE[status] },
    matchedOn: policyMatchedOn(hit),
    date: formatEffectiveDateRange({ effectiveDate: hit.effectiveDate, endDate: hit.endDate })
  };
};

// `canOpen` mirrors the classic offer rows: only staff who can quote, or view-only staff, get a link.
export const offerRow = (offer, { canOpen }) => {
  const quote = offer.quote ?? {};
  const eligibleOffers = deriveEligibleOffers(offer.options);
  return {
    key: `offer-${offer.id}`,
    type: 'Offer',
    name: joinPresent(
      [joinPresent([quote.fname, quote.lname], ' '), eligibleOffers === 'N/A' ? '' : eligibleOffers],
      ' · '
    ),
    location: joinPresent([quote.submittedAddress?.city, quote.submittedAddress?.state], ', '),
    id: offer.id,
    to: canOpen ? staffOfferLink(offer.id) : null,
    status: null,
    matchedOn: offerMatchedOn(offer),
    date: offer.updatedDateTime ? dateTimeFormatterInUserTimezone(offer.updatedDateTime) : ''
  };
};

export const branchPlusRow = (policy) => ({
  key: `branch-plus-${policy.policy_id}`,
  type: 'Branch Plus',
  name: joinPresent(
    [joinPresent([policy.customer_first_name, policy.customer_last_name], ' '), policy.policy_type],
    ' · '
  ),
  location: joinPresent([policy.carrier, policy.address, policy.state], ', '),
  id: policy.policy_id,
  to: policy.account_id
    ? `/customer/${policy.account_id}/branch-plus/${policy.policy_id}`
    : `/branch-plus/${policy.policy_id}`,
  status: null,
  matchedOn: '',
  date: '',
  carrierLoginUrl: policy.login_url
});
